'use client'

import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { focusRing } from '@repo/studio-design-system/utilities'
import { useIsMounted } from 'hooks/use-is-mounted'
import { Slot } from 'primitives/slot'
import { tw } from 'utils/classnames'
import { LoadingSpinner } from './loading-spinner'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'ghost'
  | 'overlay'
  | 'danger'
  | 'promotional'
export type ButtonSize = 'xs' | 'sm' | 'md'

interface ButtonRootProps {
  variant?: ButtonVariant
  size?: ButtonSize
  asChild?: boolean
  loading?: boolean
}

export type ButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> &
  ButtonRootProps & {
    ref?: React.Ref<HTMLButtonElement>
  }

function ButtonRoot({
  size = 'md',
  variant = 'primary',
  asChild,
  loading,
  ref,
  ...props
}: ButtonProps) {
  const className = tw(
    'rounded-2 outline-none',
    focusRing,
    'relative isolate',
    'flex items-center justify-center',
    props.disabled && 'cursor-not-allowed',
    buttonClasses.sizes[size],
    buttonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  if (loading) {
    hasSetLoading.current = true
  }

  return (
    <>
      <Comp {...props} className={tw(className)} ref={innerRef} />
      {hasSetLoading.current ? (
        <ButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}

function ButtonLoader({
  containerRef,
  loading,
  size,
}: {
  containerRef?: React.RefObject<HTMLButtonElement | null>
  loading?: boolean
  size: ButtonSize
}) {
  const isMounted = useIsMounted()

  if (!isMounted || !containerRef?.current) {
    return null
  }

  return ReactDOM.createPortal(
    <LoadingSpinner loading={loading} size={size} />,
    containerRef.current,
  )
}

export const Button = ButtonRoot

const buttonClasses = {
  variants: {
    primary: [
      'bg-theme_component_action_button_primary_background-color-default text-theme_component_action_button_primary_text-foreground-color-default',
      'hover:bg-theme_component_action_button_primary_background-color-hover',
      'active:bg-theme_component_action_button_primary_background-color-active',
      'disabled:bg-theme_component_action_button_primary_background-color-disabled disabled:text-theme_component_action_button_primary_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_primary_background--color-focus',
    ].join(' '),
    secondary: [
      'bg-theme_component_action_button_secondary_background-color-default text-theme_component_action_button_secondary_text-foreground-color-default',
      'hover:bg-theme_component_action_button_secondary_background-color-hover',
      'active:bg-theme_component_action_button_secondary_background-color-active',
      'disabled:bg-theme_component_action_button_secondary_background-color-disabled disabled:text-theme_component_action_button_secondary_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_secondary_background--color-focus',
    ].join(' '),
    tertiary: [
      'bg-transparent border border-theme_component_action_button_tertiary_border-color-default text-theme_component_action_button_tertiary_text-foreground-color-default',
      'hover:bg-theme_component_action_button_tertiary_background-color-hover',
      'active:bg-theme_component_action_button_tertiary_background-color-active',
      'disabled:border-theme_component_action_button_tertiary_border-color-disabled disabled:text-theme_component_action_button_tertiary_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_tertiary_background--color-focus',
    ].join(' '),
    ghost: [
      'bg-transparent text-theme_component_action_button_ghost_text-foreground-color-default',
      'hover:bg-theme_component_action_button_ghost_background-color-hover',
      'active:bg-theme_component_action_button_ghost_background-color-active',
      'disabled:bg-theme_component_action_button_ghost_background-color-disabled disabled:text-theme_component_action_button_ghost_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_ghost_background--color-focus',
    ].join(' '),
    overlay: [
      'bg-theme_component_action_button_inverse_background-color-default text-theme_component_action_button_inverse_text-foreground-color-default',
      'hover:bg-theme_component_action_button_inverse_background-color-hover',
      'active:bg-theme_component_action_button_inverse_background-color-active',
      'disabled:bg-theme_component_action_button_inverse_background-color-disabled disabled:text-theme_component_action_button_inverse_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_inverse_background--color-focus',
    ].join(' '),
    danger: [
      'bg-theme_component_action_button_danger_background-color-default text-theme_component_action_button_danger_text-foreground-color-default',
      'hover:bg-theme_component_action_button_danger_background-color-hover',
      'active:bg-theme_component_action_button_danger_background-color-active',
      'disabled:bg-theme_component_action_button_danger_background-color-disabled disabled:text-theme_component_action_button_danger_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_danger_background-color-focus',
    ].join(' '),
    promotional: [
      'bg-theme_component_action_button_promotional_background-color-default text-theme_component_action_button_promotional_text-foreground-color-default',
      'hover:bg-theme_component_action_button_promotional_background-color-hover',
      'active:bg-theme_component_action_button_promotional_background-color-active',
      'disabled:bg-theme_component_action_button_promotional_background-color-disabled disabled:text-theme_component_action_button_promotional_text-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_promotional_background--color-focus',
    ].join(' '),
  },
  sizes: {
    md: ['h-10 px-4 gap-3 [&_svg]:size-6 [&_svg]:-mx-1 typography-14'].join(' '),
    sm: ['h-8 px-3 gap-2 [&_svg]:size-5 [&_svg]:-mx-1 typography-13'].join(' '),
    xs: ['h-6 px-2 gap-2 [&_svg]:size-4 [&_svg]:-mx-1 typography-12'].join(' '),
  },
}

export type IconButtonProps = Omit<React.ComponentProps<'button'>, 'ref'> & {
  size?: ButtonSize
  variant?: ButtonVariant
  asChild?: boolean
  loading?: boolean
  ref?: React.Ref<HTMLButtonElement>
}

export const IconButton = function ({
  ref,
  size = 'md',
  variant = 'primary',
  asChild,
  loading,
  ...props
}: IconButtonProps) {
  const className = tw(
    'relative isolate',
    'rounded-2 outline-none',
    focusRing,
    props.disabled && 'cursor-not-allowed',
    iconButtonClasses.sizes[size],
    iconButtonClasses.variants[variant],
    '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:ease-in-out',
    props.className,
  )

  const Comp = asChild ? Slot : 'button'

  const innerRef = React.useRef<HTMLButtonElement>(null)
  React.useImperativeHandle(ref, () => innerRef.current!)

  const hasSetLoading = React.useRef(false)
  if (loading) {
    hasSetLoading.current = true
  }

  return (
    <>
      <Comp {...props} className={className} ref={innerRef} />
      {hasSetLoading.current ? (
        <ButtonLoader containerRef={innerRef} loading={loading} size={size} />
      ) : null}
    </>
  )
}
IconButton.displayName = 'IconButton'

const iconButtonClasses = {
  variants: {
    primary: [
      'bg-theme_component_action_button_primary_background-color-default text-theme_component_action_button_primary_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_primary_background-color-hover',
      'active:bg-theme_component_action_button_primary_background-color-active',
      'disabled:bg-theme_component_action_button_primary_background-color-disabled disabled:text-theme_component_action_button_primary_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_primary_background--color-focus',
    ].join(' '),
    secondary: [
      'bg-theme_component_action_button_secondary_background-color-default text-theme_component_action_button_secondary_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_secondary_background-color-hover',
      'active:bg-theme_component_action_button_secondary_background-color-active',
      'disabled:bg-theme_component_action_button_secondary_background-color-disabled disabled:text-theme_component_action_button_secondary_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_secondary_background--color-focus',
    ].join(' '),
    tertiary: [
      'bg-transparent border border-theme_component_action_button_tertiary_border-color-default text-theme_component_action_button_tertiary_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_tertiary_background-color-hover',
      'active:bg-theme_component_action_button_tertiary_background-color-active',
      'disabled:border-theme_component_action_button_tertiary_border-color-disabled disabled:text-theme_component_action_button_tertiary_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_tertiary_background--color-focus',
    ].join(' '),
    ghost: [
      'bg-transparent text-theme_component_action_button_ghost_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_ghost_background-color-hover',
      'active:bg-theme_component_action_button_ghost_background-color-active',
      'disabled:bg-theme_component_action_button_ghost_background-color-disabled disabled:text-theme_component_action_button_ghost_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_ghost_background--color-focus',
    ].join(' '),
    overlay: [
      'bg-theme_component_action_button_inverse_background-color-default text-theme_component_action_button_inverse_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_inverse_background-color-hover',
      'active:bg-theme_component_action_button_inverse_background-color-active',
      'disabled:bg-theme_component_action_button_inverse_background-color-disabled disabled:text-theme_component_action_button_inverse_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_inverse_background--color-focus',
    ].join(' '),
    danger: [
      'bg-theme_component_action_button_danger_background-color-default text-theme_component_action_button_danger_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_danger_background-color-hover',
      'active:bg-theme_component_action_button_danger_background-color-active',
      'disabled:bg-theme_component_action_button_danger_background-color-disabled disabled:text-theme_component_action_button_danger_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_danger_background-color-focus',
    ].join(' '),
    promotional: [
      'bg-theme_component_action_button_promotional_background-color-default text-theme_component_action_button_promotional_icon-foreground-color-default',
      'hover:bg-theme_component_action_button_promotional_background-color-hover',
      'active:bg-theme_component_action_button_promotional_background-color-active',
      'disabled:bg-theme_component_action_button_promotional_background-color-disabled disabled:text-theme_component_action_button_promotional_icon-foreground-color-disabled',
      'focus-visible:bg-theme_component_action_button_promotional_background--color-focus',
    ].join(' '),
  },
  sizes: {
    md: ['flex h-10 w-10 items-center justify-center px-2 [&>svg]:size-6'].join(' '),
    sm: ['flex h-8 w-8 items-center justify-center px-1 [&>svg]:size-5'].join(' '),
    xs: ['flex h-6 w-6 items-center justify-center px-1 [&>svg]:size-4'].join(' '),
  },
}
